import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App.js';
import { AuthProvider } from './auth/AuthContext.js';
import { ConfigProvider } from './config/ConfigContext.js';
import AnalyticsBootstrap from './lib/analytics/AnalyticsBootstrap.js';

// i18next bootstrap — imported for its side effect (i18n.init) BEFORE render so
// t() is ready on first paint. Catalogs are bundled JSON (no runtime fetch).
import './i18n/index.js';
import { LocaleProvider } from './i18n/LocaleContext.js';

// Inter — the one self-hosted UI typeface, matching the Nexus frontend for
// cross-app visual continuity. Bundled via @fontsource (no CDN / runtime
// third-party request). Weights mirror Nexus: 400/500/600/700.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

import './styles.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root element');

// A focused type="number" input steps its value when the wheel turns over it,
// so scrolling the page past a decimal size box silently changes it. Dropping
// focus first lets the wheel scroll the page and leaves the value alone.
document.addEventListener(
  'wheel',
  (e) => {
    const el = document.activeElement;
    if (el instanceof HTMLInputElement && el.type === 'number' && e.target === el) el.blur();
  },
  { passive: true },
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      // CLAUDE.md §No caching of pricing data — TanStack Query caches
      // GET responses for stale-while-revalidate. For quotes + schema,
      // that's fine. The pricing call is a POST and isn't cached by
      // TanStack at all (mutations live outside the query cache).
      // No exception needed here.
      staleTime: 30 * 1000,
    },
  },
});

// Top-level fallback shown when an unhandled render error escapes the
// app — the customer sees a calm "something went wrong" page instead of
// a white screen.
function FallbackUi({ resetError }: { resetError: () => void }): JSX.Element {
  return (
    <div style={{ padding: 32, fontFamily: 'sans-serif' }}>
      <h2>Something went wrong.</h2>
      <p>We&apos;re looking into it. You can try again, or refresh the page.</p>
      <button type="button" onClick={resetError}>
        Try again
      </button>
    </div>
  );
}

// Minimal top-level error boundary (vanilla React — no telemetry vendor).
// Catches unhandled render errors and shows FallbackUi instead of a blank
// screen; logs to the console so a crash isn't silent in dev.
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  override state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown): void {
    console.error('Unhandled render error:', error);
  }

  private readonly reset = (): void => this.setState({ hasError: false });

  override render(): React.ReactNode {
    if (this.state.hasError) return <FallbackUi resetError={this.reset} />;
    return this.props.children;
  }
}

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <LocaleProvider>
            <ConfigProvider>
              <AuthProvider>
                {/* Analytics/APM driver (ADR-014). Inside Router + Config + Auth
                    so useLocation / runtime keys / user identity all resolve;
                    renders nothing and is inert until keys are served. */}
                <AnalyticsBootstrap />
                <App />
              </AuthProvider>
            </ConfigProvider>
          </LocaleProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
